import {Injectable} from '@angular/core';
import {UntilDestroy} from '@ngneat/until-destroy';
import {ViewerReadyParams} from '@mydocma/bim-viewer';
import {BehaviorSubject, Subject, Subscription} from 'rxjs';
import {MarkerIconTypeEnum} from '@mydocma/tile-viewer';
import {CreationPopupComponent} from '../../creation-popup/creation-popup.component';
import {Pin} from '@mydocma/bim-viewer';
import {Guid} from 'guid-typescript';
import {CanvasClickHitEvent} from '@mydocma/bim-viewer';
import {PlanLocationService} from './plan-location.service';
import {PopupTypeEnum} from '@mydocma/bim-viewer';

@UntilDestroy()
@Injectable()
export class ObjectSelectionService {
  private bimViewerParamsData: ViewerReadyParams;
  private objectClickSubscription: Subscription;

  private selectedPinObjectSubject = new BehaviorSubject<Pin>(null);
  public selectedPinObject$ = this.selectedPinObjectSubject.asObservable();


  private objectSelectionPopupCloseSubject = new Subject<boolean>();
  public objectSelectionPopupClose = this.objectSelectionPopupCloseSubject.asObservable();
  private planLocationMovementSubscription: Subscription;

  constructor(private planLocationService: PlanLocationService) {
  }

  get selectedObject(): Pin {
    return this.selectedPinObjectSubject.value;
  }

  public attach(bimViewerParamsData): void {
    this.bimViewerParamsData = bimViewerParamsData;
    this.initializeObjectClickSubscription();
    this.initializePlanLocationMovementSubscription();
  }

  public closeSelectionPopup() {
    const currentObjectSelectedPin = this.selectedPinObjectSubject.value;
    if (currentObjectSelectedPin) {
      this.removeObjectSelectionPin(currentObjectSelectedPin);
      this.bimViewerParamsData.viewerService.forgeViewer.clearSelection();
    }

  }

  public disableObjectSelectionMode() {

    this.planLocationMovementSubscription?.unsubscribe();
    this.objectClickSubscription?.unsubscribe();
    this.selectedPinObjectSubject.next(null);

  }

  public resetSelection() {
    if (this.selectedObject) {
      this.bimViewerParamsData.popupService.closePopup(this.selectedObject.id);
      this.selectedPinObjectSubject.next(null);
    }
  }

  public enableObjectSelectionMode() {
    this.initializeObjectClickSubscription();
    this.initializePlanLocationMovementSubscription();
  }

  private initializePlanLocationMovementSubscription() {
    this.planLocationMovementSubscription?.unsubscribe();
    this.planLocationMovementSubscription = this.planLocationService.onMarkerLocationChange.subscribe(() => {
      this.closeSelectionPopup();
    });
  }


  //  replace with set unset ,
  private initializeObjectClickSubscription(): void {
    this.objectClickSubscription?.unsubscribe();
    this.objectClickSubscription = this.bimViewerParamsData?.eventListenerService.onCanvasClickHit.subscribe(value => {
      this.objectClickHandler(value);
    });
  }

  private removeObjectSelectionPin(pin: Pin): void {
    const {popupService, pinService, viewerService} = this.bimViewerParamsData;
    popupService.closePopup(pin.id);
    pinService.removePin(pin);
    if (viewerService.propertyVisibility) {
      viewerService.hidePropertiesForObject();
    }
    this.selectedPinObjectSubject.next(null);
  }


  private objectClickHandler(value: CanvasClickHitEvent) {
    const {popupService, pinService} = this.bimViewerParamsData;
    const previousSelectedPinObject = this.selectedPinObjectSubject.value;
    if (previousSelectedPinObject) {
      this.removeObjectSelectionPin(previousSelectedPinObject);
    }
    const pin = this.createPinFormEventDetails(value.detail);
    pinService.addPin(pin);
    popupService.showPopup(pin.id, {popupType: PopupTypeEnum.default});
    this.selectedPinObjectSubject.next(pin);
  }

  private createPinFormEventDetails({worldCoords, selectedElemId}): Pin {
    return {
      id: Guid.create().toString(),
      point: worldCoords,
      markerOptions: {
        iconType: MarkerIconTypeEnum.default,
        className: 'object-selection-pin',
        disablePopupMouseEvents: true,
      },
      draggable: false,
      bimElementId: selectedElemId,
      popupComponent: CreationPopupComponent,
      camera: this.bimViewerParamsData.cameraService.getCameraPosition()
    } as unknown as Pin;
  }
}
